import React from "react";
import type { GameData, FootnoteVariant } from "@/interfaces";
import { getFootnoteConfig, type FootnoteConfig } from "@/lib/footnote-config";
import { useAnalytics } from "@/hooks/useAnalytics";

interface FootnoteContentProps {
  gameData?: GameData;
  isCaliforniaUser?: boolean;
  textClassName?: string;
  linkClassName?: string;
  showPoweredBy?: boolean;
  poweredByClassName?: string;
  imageClassName?: string;
}

/**
 * Map link text to analytics cta_name
 */
function getLinkCtaName(linkText: string): string {
  const lowerText = linkText.toLowerCase();
  if (lowerText.includes('eula') || lowerText.includes('terms')) {
    return 'eula';
  }
  if (lowerText.includes('refund')) {
    return 'refund policy';
  }
  if (lowerText.includes('privacy')) {
    return 'privacy policy';
  }
  return lowerText;
}

/**
 * Renders the disclaimer text with links interpolated
 */
function renderDisclaimerWithLinks(
  config: FootnoteConfig,
  linkClassName: string = "underline",
  onLinkClick?: (ctaName: string) => void
): React.ReactNode {
  const { disclaimerText, links } = config;

  // Create a map of link placeholders to their JSX elements
  const linkMap: Record<string, React.ReactNode> = {};

  // Map placeholder names to links based on their position in the array
  // For superplay: {termsLink}
  // For stash: {eulaLink}, {refundLink}, {privacyLink}
  const placeholderPatterns = disclaimerText.match(/\{(\w+)\}/g) || [];

  placeholderPatterns.forEach((placeholder, index) => {
    if (links[index]) {
      const key = placeholder.slice(1, -1); // Remove { and }
      const ctaName = getLinkCtaName(links[index].text);
      linkMap[key] = (
        <a
          key={key}
          href={links[index].url}
          className={linkClassName}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            try {
              onLinkClick?.(ctaName);
            } catch {
              // Ignore tracking errors
            }
          }}
        >
          {links[index].text}
        </a>
      );
    }
  });

  // Split the disclaimer text by placeholders and interleave with links
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let partIndex = 0;

  const regex = /\{(\w+)\}/g;
  let match;

  while ((match = regex.exec(disclaimerText)) !== null) {
    // Add text before the placeholder
    if (match.index > lastIndex) {
      parts.push(
        <React.Fragment key={`text-${partIndex}`}>
          {disclaimerText.slice(lastIndex, match.index)}
        </React.Fragment>
      );
      partIndex++;
    }

    // Add the link
    const key = match[1];
    if (linkMap[key]) {
      parts.push(linkMap[key]);
      partIndex++;
    }

    lastIndex = match.index + match[0].length;
  }

  // Add any remaining text after the last placeholder
  if (lastIndex < disclaimerText.length) {
    parts.push(
      <React.Fragment key={`text-${partIndex}`}>
        {disclaimerText.slice(lastIndex)}
      </React.Fragment>
    );
  }

  return <>{parts}</>;
}

/**
 * FootnoteContent component that renders the configurable footnote
 * based on the footnote_variant in gameData
 */
const FootnoteContent: React.FC<FootnoteContentProps> = ({
  gameData,
  isCaliforniaUser = false,
  textClassName = "",
  linkClassName = "underline",
  showPoweredBy = true,
  poweredByClassName = "",
  imageClassName,
}) => {
  const variant: FootnoteVariant = gameData?.footnote_variant || "superplay";
  const config = getFootnoteConfig(variant, isCaliforniaUser);
  const { track } = useAnalytics();
  
  // Default image size: smaller for stash variant
  const defaultImageClass = variant === 'stash' ? 'h-4' : 'h-6';
  const imgClass = imageClassName ?? defaultImageClass;

  const handleLinkClick = (ctaName: string) => {
    track('click_in_checkout', { cta_name: ctaName });
  };

  return (
    <>
      <p className={textClassName}>
        {renderDisclaimerWithLinks(config, linkClassName, handleLinkClick)}
      </p>
      {showPoweredBy && config.poweredByImage && (
        <div className={`flex justify-center mt-${variant === 'stash' ? '1' : '3'} ${poweredByClassName}`}>
          <img
            src={config.poweredByImage}
            alt={config.poweredByAlt}
            className={`${imgClass} object-contain`}
          />
        </div>
      )}
    </>
  );
};

export default React.memo(FootnoteContent);

